import { BRAND_COLORS } from "@/Utils/brandColors";
import { useEffect, useMemo } from "react";
import { useCustomNavigation } from "@/hooks/useCustomNavigation";
import { AnimatedFigureIcon } from "@/Components/AvatarLifeCycle";
import {
  clearAvatarSharingData,
  getAvatarSharingData,
} from "@/Utils/avatarSharingStorage";
import RedirectProgress from "@/Components/RedirectProgress";

import "./sendMeasurements.scss";

// Green check for the "sent" status row (matches the handoff: 12×12 viewBox,
// #00A931 stroke, rounded caps). Mirrors the sibling SubmitScan screen.
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M2.5 6.2 L5 8.6 L9.5 3.6"
      stroke={BRAND_COLORS.success}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SendMeasurements = () => {
  const { navigateTo } = useCustomNavigation();

  // Capture org name + redirect URL once, on mount — the 500ms cleanup below
  // wipes the sharing data, and RedirectProgress keeps its own captured copy.
  // orgName falls back to the default brand when there's no sharing session
  // (mirrors SubmitScan), replacing the design's hardcoded "TrueToForm".
  const { orgName, redirectUrl } = useMemo(() => {
    const data = getAvatarSharingData();
    return {
      orgName: data.orgName || "TrueToForm",
      redirectUrl: data.redirectUrl || null,
    };
  }, []);

  useEffect(() => {
    // wait to display the orgName first then clear the sharing data
    const timer = setTimeout(() => {
      clearAvatarSharingData();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    navigateTo("/");
  };

  return (
    <div className="send-measurements">
      <div className="send-measurements__main">
        {/*
          Already-approved scan: nothing is uploading, so the figure is shown
          in its terminal green "sent" state (no bottom-to-top fill animation).
        */}
        <div className="send-measurements__figure">
          <AnimatedFigureIcon color={BRAND_COLORS.success} fillPercent={100} submitted />
        </div>

        <h1 className="send-measurements__title">Measurements sent!</h1>
        <p className="send-measurements__subtitle">
          {redirectUrl
            ? `We'll take you back to ${orgName} shortly.`
            : // Standalone share (opened outside the widget flow, e.g. scanning
              // the standalone QR code): there's no widget to return to, so don't
              // promise a redirect — point the user at their avatars list, where
              // the "View my avatars" footer link below takes them.
              "You can return to your avatars list now."}
        </p>

        <div className="send-measurements__status">
          <div className="send-measurements__status-row">
            <span className="send-measurements__status-icon">
              <CheckIcon />
            </span>
            <span className="send-measurements__status-label">
              Measurements sent to <b>{orgName}</b>
            </span>
          </div>
        </div>
      </div>

      <div className="send-measurements__footer">
        {redirectUrl ? (
          // Slim redirect bar + "Continue now" — reuses SubmitScan's minimal
          // variant (iOS-safe redirect logic), bound to the real countdown.
          <div className="send-measurements__footer-success">
            <RedirectProgress variant="minimal" durationSeconds={4} />
          </div>
        ) : (
          // No redirect URL (opened outside the widget flow): handleClose
          // navigates to "/", the avatars list, so label it accordingly.
          <button
            type="button"
            className="send-measurements__close send-measurements__close--link"
            onClick={handleClose}
          >
            View my avatars
          </button>
        )}
      </div>
    </div>
  );
};

export default SendMeasurements;
